import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 120).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const dialog = this.closest('.cart-drawer__dialog');

      // Drawer: skip view transitions — they clear drawer backgrounds and flash the page overlay.
      if (this.isDrawer) {
        dialog?.classList.add('cart-drawer--empty');
        this.replaceChildren(clone);
        return;
      }

      startViewTransition(() => {
        dialog?.classList.add('cart-drawer--empty');
        this.replaceChildren(clone);
      }, ['empty-cart-page']);

      return;
    }

    // Drawer: hide instantly after this click finishes (removing mid-click closes the dialog
    // because dialog treats the detached trash button as an outside click).
    if (this.isDrawer) {
      queueMicrotask(() => {
        rowsToRemove.forEach((row) => {
          if (row.isConnected) row.remove();
        });
      });
      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    window.__bhCartUpsell?.lockCheckout?.('cart');

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const initialSections = parsedResponseText.sections || {};
        const sectionHtml = initialSections[this.sectionId];

        const newSectionHTML = sectionHtml
          ? new DOMParser().parseFromString(sectionHtml, 'text/html')
          : null;

        // Prefer cart JSON item_count — missing HTML count must not force empty drawer.
        const parsedCountText = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const parsedCount = parsedCountText ? parseInt(parsedCountText, 10) : NaN;
        const newCartItemCount =
          typeof parsedResponseText.item_count === 'number'
            ? parsedResponseText.item_count
            : Number.isFinite(parsedCount)
              ? parsedCount
              : Array.isArray(parsedResponseText.items)
                ? parsedResponseText.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
                : undefined;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // Morph immediately — discount sync remorphs in the background (faster add/remove).
        if (sectionHtml) {
          morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
          if (newCartItemCount != null) {
            this.#syncDrawerEmptyClass(newCartItemCount);
          }
          this.#updateCartQuantitySelectorButtonStates();
        }

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount ?? 0,
            source: 'cart-items-component',
            sections: initialSections,
          })
        );

        void this.#resolveCartSections(parsedResponseText).then((sections) => {
          const syncedHtml = sections?.[this.sectionId];
          if (syncedHtml && syncedHtml !== sectionHtml) {
            morphSection(this.sectionId, syncedHtml, this.isDrawer ? 'hydration' : 'full');
            this.#updateCartQuantitySelectorButtonStates();
          }
          window.__bhCartUpsell?.refreshUpsellPricesFromCart?.(parsedResponseText);
        });
      })
      .catch((error) => {
        console.error(error);
        window.__bhCartUpsell?.unlockCheckout?.('cart');
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        // Safety unlock if cart:update unlock path is missed.
        window.setTimeout(() => window.__bhCartUpsell?.unlockCheckout?.('cart'), 8000);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    window.__bhCartUpsell?.unlockCheckout?.('cart');

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      // Morph immediately so upsell ADD feels instant; discount sync remorphs in the background.
      morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full');
      this.#syncDrawerEmptyClass(event.detail.data?.itemCount);
      this.#updateCartQuantitySelectorButtonStates();

      void this.#resolveCartSections({
        ...event.detail.resource,
        sections: event.detail.data.sections,
      }).then((sections) => {
        const syncedHtml = sections?.[this.sectionId];
        if (syncedHtml && syncedHtml !== cartItemsHtml) {
          morphSection(this.sectionId, syncedHtml, this.isDrawer ? 'hydration' : 'full');
          this.#updateCartQuantitySelectorButtonStates();
        }
        window.__bhCartUpsell?.refreshUpsellPricesFromCart?.(event.detail.resource);
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Syncs upsell discount before cart HTML is shown so totals match checkout.
   *
   * @param {object} cartResponse
   * @returns {Promise<Record<string, string>>}
   */
  async #resolveCartSections(cartResponse) {
    const ensure = window.__bhCartUpsell?.ensureDiscountSynced;
    if (typeof ensure !== 'function') return cartResponse.sections;

    const synced = await ensure(cartResponse);
    return synced?.sections || cartResponse.sections;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Keep drawer empty class on <dialog> when using hydration morph (dialog itself is not remorphed).
   * @param {number | undefined} itemCount
   */
  #syncDrawerEmptyClass(itemCount) {
    if (!this.isDrawer || itemCount == null) return;
    this.closest('.cart-drawer__dialog')?.classList.toggle('cart-drawer--empty', itemCount === 0);
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
